import React, { Component } from 'react';
import { connect } from 'react-redux';
import Dashboard from '../Dashboard';
import SectionHeader from '../elements/SectionHeader';
import ModuleHeader from '../elements/ModuleHeader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import server from '../../../api/server';
import { Accordion } from '../elements/Accordion';
import PricingTable from '../PricingTable';
import PremiumSupport from '../PremiumSupport';
import axios from 'axios';
import ReactGA from 'react-ga4';
import { setAuth } from '../../../actions';
import moment from 'moment';
import PremiumModal from '../elements/PremiumModal';
import PremiumUpgradeModal from '../elements/PremiumUpgradeModal';
import SubscriptionChangeModal from '../elements/SubscriptionChangeModal';

// Custom CSS for the toggle
const toggleStyles = `
  .premium-toggle {
    width: 64px;
    height: 34px;
    border-radius: 17px;
    background: #2A2A2A;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .premium-toggle.checked {
    background: #FFD700;
  }

  .premium-toggle .toggle-circle {
    width: 28px;
    height: 28px;
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 3px;
    transition: all 0.3s ease;
  }
  
  @keyframes slow-bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
  
  .slow-bounce {
    animation: slow-bounce 3s infinite ease-in-out;
  }
`;

export class PremiumTesting extends Component {
    constructor(props) {
        super(props);

        // Check if there's an active sale from Redux store
        const hasActiveSale = !!props.active_sale;
        const salePercentage = hasActiveSale ? props.active_sale.percent : 40;
        const saleEndDate = hasActiveSale && props.active_sale.expires_at ?
            moment(props.active_sale.expires_at) : moment().add(3, 'days');

        this.state = {
            processing: false,
            currency: "USD",
            unlimitedBots: false,
            priorityHostingAdded: false,
            yearlyBilling: false,
            showSingleBotModal: false,
            // Sale configuration
            saleActive: hasActiveSale,
            saleDiscount: salePercentage, // From props.active_sale or default 40% off
            saleAppliesTo: hasActiveSale ? 'both' : 'unlimited', // Based on sale_items in active_sale
            saleEndsIn: saleEndDate, // From props.active_sale.expires_at or default
            showChangeSubscriptionModal: false,
            changeSubscriptionDetails: null
        };
    }

    componentDidMount() {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
        this.setCurrency();

        // Initialize the billing cycle based on user's existing subscription
        if (this.props.user && this.props.user.premium_billing_cycle === 'yearly') {
            this.setState({ yearlyBilling: true });
        }

        // Check props.active_sale to set up sale state correctly
        this.initializeSaleState();
    }

    componentDidUpdate(prevProps) {
        // If the active_sale prop changes, update the state
        if (prevProps.active_sale !== this.props.active_sale) {
            this.initializeSaleState();
        }
    }

    initializeSaleState = () => {
        const { active_sale } = this.props;

        if (active_sale) {
            // Determine which plans the sale applies to
            let saleAppliesTo = 'both';
            if (active_sale.sale_items) {
                const singlePlanIncluded = active_sale.sale_items.monthly ||
                    active_sale.sale_items.yearly ||
                    active_sale.sale_items.lifetime;

                const unlimitedPlanIncluded = active_sale.sale_items.monthly_unlimited ||
                    active_sale.sale_items.yearly_unlimited ||
                    active_sale.sale_items.lifetime_unlimited;

                if (singlePlanIncluded && !unlimitedPlanIncluded) {
                    saleAppliesTo = 'single';
                } else if (!singlePlanIncluded && unlimitedPlanIncluded) {
                    saleAppliesTo = 'unlimited';
                }
            }

            this.setState({
                saleActive: true,
                saleDiscount: active_sale.percent || 40,
                saleAppliesTo: saleAppliesTo,
                saleEndsIn: active_sale.expires_at ? moment(active_sale.expires_at) : moment().add(3, 'days')
            });
        } else {
            // Check if there's a personal sale instead
            if (this.props.personal_sale) {
                this.setState({
                    saleActive: true,
                    saleDiscount: 40, // Personal sales are typically 40% off
                    saleAppliesTo: 'both',
                    saleEndsIn: moment().add(3, 'days') // Personal sales usually have a short expiry
                });
            } else {
                this.setState({ saleActive: false });
            }
        }
    };

    setCurrency = async () => {
        try {
            const results = await axios.get("https://ipapi.co/json");
            if ("data" in results && "currency" in results.data && results.data.currency === "EUR") {
                this.setState({ currency: "EURO" });
            }
        } catch (error) {
            console.error(error);
        }
    };

    handleUpgrade = async (plan) => {
        if (plan === 'single') {
            // Show the single bot confirmation modal instead of proceeding directly
            this.setState({ showSingleBotModal: true });
            return;
        }

        this.setState({ processing: true });

        try {
            // Track analytics
            ReactGA.event({
                category: 'Subscription',
                action: 'Initiated Checkout',
                label: `${plan} - ${this.state.yearlyBilling ? 'yearly' : 'monthly'}`
            });

            const billingCycle = this.state.yearlyBilling ? 'yearly' : 'monthly';

            // Create checkout data with appropriate metadata
            const checkoutData = {
                planType: plan,
                billingCycle: billingCycle,
            };

            // If it's a single bot plan, include the bot_id in the metadata
            if (plan === 'single' && this.props.bot) {
                if (this.props.bot.bot_id) {
                    checkoutData.bot_id = this.props.bot.bot_id;
                    console.log(`Including bot_id in checkout: ${this.props.bot.bot_id}`);
                } else if (this.props.bot.id) {
                    // Fallback to id if bot_id is not available
                    checkoutData.bot_id = this.props.bot.id;
                    console.log(`Including bot.id in checkout (fallback): ${this.props.bot.id}`);
                } else {
                    console.log('Warning: No bot_id or id found in bot object');
                    console.log('Bot object:', JSON.stringify(this.props.bot));
                }
            }

            // Call the premium checkout endpoint
            const response = await server.post('/dashboard/premium/checkout', checkoutData);

            if (response.data && response.data.url) {
                // Immediately redirect to Stripe checkout URL
                window.location.href = response.data.url;
            } else {
                toast.error('Failed to create checkout session.');
                this.setState({ processing: false });
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            toast.error('An error occurred while processing your request. Please try again.');
            this.setState({ processing: false });
        }
    };

    closeSingleBotModal = () => {
        this.setState({ showSingleBotModal: false });
    };

    // Helper method to get the button text for the single plan
    getSinglePlanButtonText = () => {
        const { user } = this.props;
        const { yearlyBilling } = this.state;

        // If user has no subscription
        if (!user || !user.premium_active) {
            return `Upgrade ${this.props.bot?.name || 'this bot'}`;
        }

        // If user has single plan subscription
        if (user.premium_type === 'single') {
            // If viewing the same billing cycle as their current plan
            if ((yearlyBilling && user.premium_billing_cycle === 'yearly') ||
                (!yearlyBilling && user.premium_billing_cycle === 'monthly')) {
                return 'Manage Subscription';
            } else if (!yearlyBilling && user.premium_billing_cycle === 'yearly') {
                // If they have yearly and are viewing monthly (downgrade not allowed)
                return 'Contact Support to Downgrade';
            } else {
                // If they have monthly and are viewing yearly (upgrade allowed)
                return 'Upgrade to Yearly';
            }
        }

        // If user has unlimited plan subscription
        return 'Contact Support to Downgrade';
    };

    // Helper method to get the button text for the unlimited plan
    getUnlimitedPlanButtonText = () => {
        const { user } = this.props;
        const { yearlyBilling } = this.state;

        // If user has no subscription
        if (!user || !user.premium_active) {
            return 'Upgrade Unlimited Bots';
        }

        // If user has single plan subscription
        if (user.premium_type === 'single') {
            return 'Upgrade to Unlimited';
        }

        // If user has unlimited plan subscription
        if ((yearlyBilling && user.premium_billing_cycle === 'yearly') ||
            (!yearlyBilling && user.premium_billing_cycle === 'monthly')) {
            return 'Manage Subscription';
        } else if (!yearlyBilling && user.premium_billing_cycle === 'yearly') {
            // If they have yearly and are viewing monthly (downgrade not allowed)
            return 'Contact Support to Downgrade';
        } else {
            // If they have monthly and are viewing yearly (upgrade allowed)
            return 'Upgrade to Yearly';
        }
    };

    // Helper to handle cancellation
    handleCancelSubscription = async () => {
        this.setState({ processing: true });

        try {
            const response = await server.post('/dashboard/premium/cancel');

            if (response.data && response.data.success) {
                toast.success('Your subscription has been canceled.');

                // Update user data
                if (this.props.setAuth) {
                    this.props.setAuth({
                        ...this.props.auth,
                        user: {
                            ...this.props.user,
                            premium_active: false,
                            premium_subscription_id: null,
                            premium_type: null
                        }
                    });
                }
            } else {
                toast.error('Failed to cancel subscription. Please try again or contact support.');
            }
        } catch (error) {
            console.error('Error canceling subscription:', error);
            toast.error('An error occurred while canceling your subscription. Please try again.');
        }

        this.setState({ processing: false });
    };

    // Helper to handle subscription action (upgrade, change, or cancel)
    handleSubscriptionAction = (plan) => {
        const { user } = this.props;
        const { yearlyBilling } = this.state;

        // Don't allow downgrading from Unlimited to Single plan
        if (user?.premium_active && user.premium_type === 'unlimited' && plan === 'single') {
            toast.error('Downgrading from Unlimited to Single is not supported. Please contact support for assistance.');
            return;
        }

        // Don't allow downgrading from Yearly to Monthly billing cycle
        if (user?.premium_active && user.premium_billing_cycle === 'yearly' && !yearlyBilling) {
            toast.error('Downgrading from Yearly to Monthly billing is not supported. Please contact support for assistance.');
            return;
        }

        // If user has the same plan and billing cycle, send to Stripe portal
        if (user?.premium_active &&
            user.premium_type === plan &&
            (yearlyBilling ? user.premium_billing_cycle === 'yearly' : user.premium_billing_cycle === 'monthly')) {
            window.open(process.env.REACT_APP_STRIPE_PORTAL, '_blank');
            return;
        }

        // For existing subscribers who want to change plan, show change modal
        if (user?.premium_active) {
            this.setState({
                showChangeSubscriptionModal: true,
                changeSubscriptionDetails: {
                    targetPlan: plan,
                    targetBilling: yearlyBilling ? 'yearly' : 'monthly'
                }
            });
        } else {
            // For new subscriptions, use the regular checkout flow
            this.handleUpgrade(plan);
        }
    };

    // Handle successful subscription change
    handleSubscriptionChangeSuccess = () => {
        toast.success('Your subscription has been updated successfully!');
        this.setState({ showChangeSubscriptionModal: false });
    };

    // Close the subscription change modal
    closeChangeSubscriptionModal = () => {
        this.setState({ showChangeSubscriptionModal: false });
    };

    // Helper to check if user has an old premium subscription
    hasOldPremiumSubscription = () => {
        const { user } = this.props;
        return user &&
            user.transactions &&
            user.transactions.length > 0 &&
            user.transactions.some(transaction => transaction.active === true);
    };

    render() {
        const { currency, processing, saleActive, saleDiscount, saleAppliesTo, saleEndsIn } = this.state;
        const { user, active_sale, personal_sale, bot, collab } = this.props;
        const botName = this.props.bot?.name || "this bot";

        // Check user's subscription status
        const hasSubscription = user && user.premium_active;
        const hasSinglePlan = hasSubscription && user.premium_type === 'single';
        const hasUnlimitedPlan = hasSubscription && user.premium_type === 'unlimited';
        const hasYearlyBilling = hasSubscription && user.premium_billing_cycle === 'yearly';
        const hasMonthlyBilling = hasSubscription && user.premium_billing_cycle === 'monthly';

        // Pricing data - updated prices
        const prices = {
            monthly: {
                single: 10,
                unlimited: 20,
            },
            yearly: {
                single: 10 * 8,
                unlimited: 20 * 8,
            }
        };

        // Calculate sale prices if sale is active
        const checkSaleApplies = (planType) => {
            // Check if there's an active global sale first
            if (active_sale && active_sale.sale_items) {
                const billingCycle = this.state.yearlyBilling ? 'yearly' : 'monthly';
                const planKey = billingCycle + (planType === 'unlimited' ? '_unlimited' : '');
                return active_sale.sale_items[planKey] === true;
            }

            // Fall back to personal sale or component state
            if (personal_sale && !active_sale) {
                return true; // Personal sales typically apply to everything
            }

            // Use the component state as last resort
            return this.state.saleActive &&
                (this.state.saleAppliesTo === 'both' || this.state.saleAppliesTo === planType);
        };

        const calculateSalePrice = (originalPrice, planType) => {
            if (checkSaleApplies(planType)) {
                // Use the active_sale percentage if available, otherwise use state
                const discountPercentage = active_sale?.percent || saleDiscount;
                return (originalPrice * (100 - discountPercentage) / 100).toFixed(2);
            }
            return originalPrice;
        };

        // Get current prices based on billing cycle
        const currentPrices = this.state.yearlyBilling ? prices.yearly : prices.monthly;

        // For display purposes - monthly equivalent of yearly prices
        const yearlyPricesMonthly = {
            single: (prices.yearly.single / 12).toFixed(2),
            unlimited: (prices.yearly.unlimited / 12).toFixed(2)
        };

        // Apply sale discount to appropriate plans
        const salePrices = {
            single: calculateSalePrice(currentPrices.single, 'single'),
            unlimited: calculateSalePrice(currentPrices.unlimited, 'unlimited')
        };

        // Monthly equivalent of discounted yearly prices
        const salePricesMonthly = {
            single: this.state.yearlyBilling ? (salePrices.single / 12).toFixed(2) : salePrices.single,
            unlimited: this.state.yearlyBilling ? (salePrices.unlimited / 12).toFixed(2) : salePrices.unlimited
        };

        // Calculate time remaining in sale
        const now = moment();
        const saleEndTime = active_sale?.expires_at ? moment(active_sale.expires_at) : saleEndsIn;
        const duration = moment.duration(saleEndTime.diff(now));
        const timeLeft = {
            days: Math.floor(duration.asDays()),
            hours: duration.hours(),
            minutes: duration.minutes(),
            seconds: duration.seconds()
        };

        // Function to format prices based on currency
        const formatPrice = (price) => {
            return `${currency === "EURO" ? "€" : "$"}${price}`;
        };

        // Common styles for cards - update to ensure consistent height and flex layout
        const commonCardStyles = "rounded-lg transition-all duration-300 flex flex-col overflow-hidden h-full transform hover:-translate-y-1 duration-200 shadow-lg";

        return (
            <Dashboard>
                <ToastContainer />

                {/* Single Bot Confirmation Modal */}
                {this.state.showSingleBotModal && (
                    <PremiumUpgradeModal
                        closeModal={this.closeSingleBotModal}
                        billingCycle={this.state.yearlyBilling ? 'yearly' : 'monthly'}
                    />
                )}

                {/* Subscription Change Modal */}
                {this.state.showChangeSubscriptionModal && (
                    <SubscriptionChangeModal
                        onClose={this.closeChangeSubscriptionModal}
                        onSuccess={this.handleSubscriptionChangeSuccess}
                        user_id={user.id}
                        targetPlan={this.state.changeSubscriptionDetails.targetPlan}
                        targetBilling={this.state.changeSubscriptionDetails.targetBilling}
                    />
                )}

                {/* Main Container */}
                <div className="max-w-7xl mx-auto px-4 py-6">
                    {/* Hero Section */}
                    <div className="text-center mb-12">
                        <span className="text-[#FF4444] text-sm font-medium tracking-wide uppercase">Premium Features</span>
                        <h1 className="text-4xl font-bold text-white mt-3 mb-4">
                            Unlock Premium Features
                        </h1>
                        <p className="text-[#E5E7EB] text-lg mb-3 max-w-2xl mx-auto">
                            Take your Discord bot to the next level with premium features and unlimited possibilities.
                        </p>
                        <p className="text-[#9CA3AF] text-sm">
                            Includes 7-day money-back guarantee. Need help? <a href="https://discord.gg/botghost" target="_blank" rel="noopener noreferrer" className="text-[#FF4444] hover:text-[#FF6666] transition-colors">Contact us</a>.
                        </p>
                    </div>

                    {/* Subscription Management Card - Only visible for users with active subscriptions */}
                    {hasSubscription && (
                        <div className="mb-8">
                            <div className="bg-[#1A1A1A] border border-[#FFD700] rounded-lg p-6 shadow-md hover:shadow-lg transition-all">
                                <div className="flex items-start space-x-4">
                                    <div className="p-3 bg-[#FFD700] rounded-full text-black">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-white">Manage Your Subscription</h3>
                                        <p className="text-[#CCCCCC] max-w-lg">
                                            You can update your payment method or cancel your subscription at any time through our secure payment portal.
                                        </p>
                                    </div>
                                    <div className="flex space-x-4 ml-4 items-start">
                                        <a
                                            href={process.env.REACT_APP_STRIPE_PORTAL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-3 bg-[#FF4444] text-white font-bold rounded-md transform transition-all duration-200 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#FF4444] focus:ring-opacity-50 flex items-center justify-center whitespace-nowrap"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                            Cancel Subscription
                                        </a>
                                        <a
                                            href={process.env.REACT_APP_STRIPE_PORTAL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold rounded-md transform transition-all duration-200 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:ring-opacity-50 flex items-center justify-center whitespace-nowrap"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                                            </svg>
                                            Manage Your Subscription
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Old Premium Subscription Card - Only visible for users with old premium subscriptions */}
                    {this.hasOldPremiumSubscription() && !hasSubscription && (
                        <div className="mb-8">
                            <div className="bg-[#1A1A1A] border border-[#9d4edd] rounded-lg p-6 shadow-md hover:shadow-lg transition-all">
                                <div className="flex items-start space-x-4">
                                    <div className="p-3 bg-[#9d4edd] rounded-full text-white">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-white">Legacy Premium Subscription</h3>
                                        <p className="text-[#CCCCCC] mb-3">
                                            You have an active legacy premium subscription. You can manage your subscription through Chargebee.
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <a
                                            href="https://botghost.chargebeeportal.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-3 bg-gradient-to-r from-[#9d4edd] to-[#c77dff] text-white font-bold rounded-md transform transition-all duration-200 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#9d4edd] focus:ring-opacity-50 flex items-center justify-center whitespace-nowrap"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                                            </svg>
                                            Manage Subscription
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Billing Toggle */}
                    <div className="flex items-center justify-center mb-10 py-4">
                        <div className="flex items-center justify-center">
                            <span className={`mr-6 text-lg font-medium ${!this.state.yearlyBilling ? 'text-white' : 'text-[#888888]'}`}>
                                Monthly
                            </span>

                            <div
                                onClick={() => {
                                    // Don't allow changing from yearly to monthly for existing subscribers
                                    if (this.props.user?.premium_active && this.props.user?.premium_billing_cycle === 'yearly' && this.state.yearlyBilling) {
                                        toast.error('Downgrading from Yearly to Monthly billing is not supported.');
                                        return;
                                    }
                                    this.setState(prevState => ({ yearlyBilling: !prevState.yearlyBilling }));
                                }}
                                className={`relative inline-block w-16 h-8 cursor-pointer ${(this.props.user?.premium_active && this.props.user?.premium_billing_cycle === 'yearly' && this.state.yearlyBilling) ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                                <div className={`w-16 h-8 rounded-full transition-colors duration-300 ${this.state.yearlyBilling ? 'bg-[#FFD700]' : 'bg-[#333333]'}`}></div>
                                <div
                                    className={`absolute top-1 left-1 bg-white w-6 h-6 rounded-full shadow-md transition-transform duration-300 ${this.state.yearlyBilling ? 'transform translate-x-8' : ''}`}
                                ></div>
                            </div>

                            <div className="flex items-center ml-6">
                                <span className={`text-lg font-medium ${this.state.yearlyBilling ? 'text-white' : 'text-[#888888]'}`}>
                                    Yearly
                                </span>
                                <span className="ml-2 text-[#FFD700] font-bold text-sm">
                                    (4 months free)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        {/* Single Bot Plan */}
                        <div className={`${commonCardStyles} bg-[#1A1A1A] border border-[#333333] hover:border-[#FF4444]`}>
                            <div className="p-8 flex flex-col h-full">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="bg-[#FF4444] text-white px-3 py-1 rounded-full text-xs font-bold">SINGLE</div>
                                    <div className="text-sm text-[#999999]">
                                        Single bot
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <div className="flex items-baseline">
                                        {checkSaleApplies('single') ? (
                                            <>
                                                <span className="text-lg line-through text-[#999999] mr-2">
                                                    {formatPrice(this.state.yearlyBilling ? yearlyPricesMonthly.single : currentPrices.single)}
                                                </span>
                                                <span className="text-5xl font-bold text-white">
                                                    {formatPrice(this.state.yearlyBilling ? salePricesMonthly.single : salePrices.single)}
                                                </span>
                                                <span className="ml-2 text-sm text-[#FF4444]">
                                                    /mo
                                                </span>
                                                <span className="ml-3 bg-[#FF4444] text-white text-xs px-2 py-1 rounded-full">
                                                    SAVE {active_sale?.percent || saleDiscount}%
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-5xl font-bold text-white">
                                                    {formatPrice(this.state.yearlyBilling ? yearlyPricesMonthly.single : currentPrices.single)}
                                                </span>
                                                <span className="ml-2 text-sm text-[#FF4444]">
                                                    /mo
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    {this.state.yearlyBilling && (
                                        <div className="text-sm text-[#999999] mt-2">
                                            {formatPrice(this.state.yearlyBilling ? salePrices.single : (salePrices.single * 12))} billed annually
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4 flex-grow">
                                    <div className="flex items-start">
                                        <div className="text-[#FFD700] text-xl mr-3">✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Single Premium Bot</h3>
                                            <p className="text-sm text-[#999999]">Premium features for one bot</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="text-[#FFD700] text-xl mr-3">✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Servers Handled</h3>
                                            <p className="text-sm text-[#999999]">Unlimited servers per bot</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="text-[#FFD700] text-xl mr-3">✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Custom Commands</h3>
                                            <p className="text-sm text-[#999999]">Unlimited custom commands</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="text-[#FFD700] text-xl mr-3">✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Custom Events</h3>
                                            <p className="text-sm text-[#999999]">Unlimited custom events</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="text-[#FFD700] text-xl mr-3">✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Custom Status</h3>
                                            <p className="text-sm text-[#999999]">BotGhost branding removed</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="text-[#FFD700] text-xl mr-3">✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Priority Support</h3>
                                            <p className="text-sm text-[#999999]">Fast assistance when needed</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={() => this.handleSubscriptionAction('single')}
                                        disabled={
                                            this.state.processing ||
                                            (this.props.user?.premium_active && this.props.user?.premium_type === 'unlimited') ||
                                            (this.props.user?.premium_active && this.props.user?.premium_billing_cycle === 'yearly' && !this.state.yearlyBilling) ||
                                            this.props.collab
                                        }
                                        className="w-full py-4 px-6 bg-gradient-to-r from-[#FF4444] to-[#FF6666] hover:from-[#FF6666] hover:to-[#FF7777] text-white font-medium rounded-md transition-all transform hover:scale-105 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed">
                                        {this.props.collab ? 'Not Available in Collab Mode' : this.state.processing ? 'Processing...' : this.getSinglePlanButtonText()}
                                    </button>

                                    <p className="text-xs text-center text-[#999999] mt-4">
                                        {this.props.user && this.props.user.premium_active && this.props.user.premium_type === 'single'
                                            ? 'Your subscription is active.'
                                            : `Billed ${this.state.yearlyBilling ? 'yearly' : 'monthly'}. Cancel anytime.`}
                                    </p>

                                    {checkSaleApplies('single') && (
                                        <div className="text-[#FFD700] text-xs font-bold mt-2 bg-[#1A1A1A] p-2 rounded-md border border-[#2A2A2A] text-center">
                                            Lock in this discount FOREVER until you cancel!
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Unlimited Plan */}
                        <div className={`${commonCardStyles} relative ${this.state.saleActive ? 'bg-[#1a1433] border-2 border-[#9d4edd] hover:border-[#9d4edd]' : 'bg-[#1A1A1A] border border-[#FFD700] hover:border-[#FFD700]'} overflow-visible`}>
                            {/* BEST VALUE label */}
                            <div className="absolute top-0 right-0 bg-[#FFD700] text-black font-bold px-4 py-1 rounded-bl-lg text-xs">
                                BEST VALUE
                            </div>

                            <div className="p-8 flex flex-col h-full">
                                {/* UNLIMITED label */}
                                <div className="flex justify-between items-center mb-4">
                                    <div className={`inline-block ${this.state.saleActive ? 'bg-[#9d4edd] text-white' : 'bg-[#FFD700] text-black'} px-3 py-1 rounded-full text-xs font-bold`}>
                                        UNLIMITED
                                    </div>
                                    <div className="text-sm text-[#999999]">
                                        Unlimited bots
                                    </div>
                                </div>

                                {/* Price display */}
                                <div className="mb-6">
                                    {checkSaleApplies('unlimited') ? (
                                        <div className="flex items-baseline mb-1">
                                            <span className="text-lg line-through text-[#999999] mr-2">
                                                {formatPrice(this.state.yearlyBilling ? yearlyPricesMonthly.unlimited : currentPrices.unlimited)}
                                            </span>
                                            <span className="text-5xl font-bold text-white">
                                                {formatPrice(this.state.yearlyBilling ? salePricesMonthly.unlimited : salePrices.unlimited)}
                                            </span>
                                            <span className="ml-2 text-sm text-[#c77dff]">
                                                /mo
                                            </span>
                                            <span className="ml-3 bg-[#9d4edd] text-white text-xs px-2 py-1 rounded-full">
                                                SAVE {active_sale?.percent || saleDiscount}%
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-baseline mb-1">
                                            <span className="text-5xl font-bold text-white">
                                                {formatPrice(this.state.yearlyBilling ? yearlyPricesMonthly.unlimited : currentPrices.unlimited)}
                                            </span>
                                            <span className="ml-2 text-sm text-[#FFD700]">
                                                /mo
                                            </span>
                                        </div>
                                    )}

                                    {this.state.yearlyBilling && (
                                        <div className="text-sm text-[#999999] mt-2">
                                            {formatPrice(this.state.yearlyBilling ? salePrices.unlimited : (salePrices.unlimited * 12))} billed annually
                                        </div>
                                    )}

                                    {/* Create bots text */}
                                    <div className="text-[#FFD700] text-sm mt-1">
                                        All current and future bots upgraded automatically
                                    </div>

                                    {checkSaleApplies('unlimited') && (
                                        <div className="text-[#FFD700] text-sm font-bold mt-2 bg-[#1A1A1A] p-2 rounded-md border border-[#2A2A2A]">
                                            Lock in this discount FOREVER until you cancel!
                                        </div>
                                    )}
                                </div>

                                {/* Feature list */}
                                <div className="space-y-4 flex-grow">
                                    <div className="flex items-start">
                                        <div className={`${this.state.saleActive ? 'text-[#c77dff]' : 'text-[#FFD700]'} text-xl mr-3 font-bold text-2xl`}>✓</div>
                                        <div>
                                            <h3 className={`${this.state.saleActive ? 'text-[#c77dff]' : 'text-[#FFD700]'} font-bold text-lg`}>UNLIMITED Bots</h3>
                                            <p className="text-sm text-white">Upgrade all current and future bots automatically</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className={`${this.state.saleActive ? 'text-[#c77dff]' : 'text-[#FFD700]'} text-xl mr-3`}>✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Servers Handled</h3>
                                            <p className={`text-sm ${this.state.saleActive ? 'text-[#e0b1ff]' : 'text-[#CCCCCC]'}`}>Unlimited servers for all bots</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className={`${this.state.saleActive ? 'text-[#c77dff]' : 'text-[#FFD700]'} text-xl mr-3`}>✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Custom Commands</h3>
                                            <p className={`text-sm ${this.state.saleActive ? 'text-[#e0b1ff]' : 'text-[#CCCCCC]'}`}>Unlimited for all your bots</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className={`${this.state.saleActive ? 'text-[#c77dff]' : 'text-[#FFD700]'} text-xl mr-3`}>✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Custom Events</h3>
                                            <p className={`text-sm ${this.state.saleActive ? 'text-[#e0b1ff]' : 'text-[#CCCCCC]'}`}>Unlimited for all your bots</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className={`${this.state.saleActive ? 'text-[#c77dff]' : 'text-[#FFD700]'} text-xl mr-3`}>✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Custom Status</h3>
                                            <p className={`text-sm ${this.state.saleActive ? 'text-[#e0b1ff]' : 'text-[#CCCCCC]'}`}>BotGhost branding removed on all bots</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className={`${this.state.saleActive ? 'text-[#c77dff]' : 'text-[#FFD700]'} text-xl mr-3`}>✓</div>
                                        <div>
                                            <h3 className="text-white font-medium">Priority Support</h3>
                                            <p className={`text-sm ${this.state.saleActive ? 'text-[#e0b1ff]' : 'text-[#CCCCCC]'}`}>Premium support for all your bots</p>
                                        </div>
                                    </div>
                                </div>

                                {/* CTA Button */}
                                <div className="mt-6">
                                    <button
                                        onClick={() => this.handleSubscriptionAction('unlimited')}
                                        disabled={
                                            this.state.processing ||
                                            (this.props.user?.premium_active && this.props.user?.premium_billing_cycle === 'yearly' && !this.state.yearlyBilling) ||
                                            this.props.collab
                                        }
                                        className={`w-full py-4 px-6 font-bold rounded-md text-lg relative overflow-hidden ${this.state.saleActive
                                            ? 'text-white bg-[#9d4edd] hover:bg-[#8a2be2]'
                                            : 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFD700] text-black'
                                            } transition-all disabled:opacity-70 disabled:cursor-not-allowed`}
                                    >
                                        {this.props.collab ? 'Not Available in Collab Mode' : this.state.processing ? 'Processing...' : this.getUnlimitedPlanButtonText()}
                                    </button>

                                    <p className="text-xs text-center text-[#999999] mt-4">
                                        {this.props.user && this.props.user.premium_active && this.props.user.premium_type === 'unlimited'
                                            ? 'Your subscription is active.'
                                            : `Billed ${this.state.yearlyBilling ? 'yearly' : 'monthly'}. Cancel anytime.`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Premium Features Section */}
                    <div className="mb-16">
                        <SectionHeader title="Premium Features" pretitle="What You Get" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                            {/* No BotGhost Branding */}
                            <div className="bg-[#1A1A1A] p-6 rounded-lg border border-[#333333] hover:border-[#FF4444] transition-all">
                                <div className="text-[#FF4444] text-2xl mb-4">🔒</div>
                                <h3 className="text-xl font-bold text-white mb-2">No BotGhost Branding</h3>
                                <p className="text-sm text-[#999999] mb-4">Remove the "Made with BotGhost" text from your bot's presence. Showcase your bot as completely your own creation.</p>
                                <div className="h-1 w-16 bg-gradient-to-r from-[#FF4444] to-[#FF6666]"></div>
                            </div>

                            {/* Unlimited Servers */}
                            <div className="bg-[#1A1A1A] p-6 rounded-lg border border-[#333333] hover:border-[#FF4444] transition-all">
                                <div className="text-[#FF4444] text-2xl mb-4">⚡</div>
                                <h3 className="text-xl font-bold text-white mb-2">Unlimited Servers</h3>
                                <p className="text-sm text-[#999999] mb-4">Run your bot on unlimited Discord servers, compared to just 5 servers on the free plan.</p>
                                <div className="h-1 w-16 bg-gradient-to-r from-[#FF4444] to-[#FF6666]"></div>
                            </div>

                            {/* Custom Status */}
                            <div className="bg-[#1A1A1A] p-6 rounded-lg border border-[#333333] hover:border-[#FF4444] transition-all">
                                <div className="text-[#FF4444] text-2xl mb-4">💬</div>
                                <h3 className="text-xl font-bold text-white mb-2">Custom Status</h3>
                                <p className="text-sm text-[#999999] mb-4">Set your own custom status message for your bot. Complete control over how your bot presents itself.</p>
                                <div className="h-1 w-16 bg-gradient-to-r from-[#FF4444] to-[#FF6666]"></div>
                            </div>

                            {/* Unlimited Custom Commands */}
                            <div className="bg-[#1A1A1A] p-6 rounded-lg border border-[#333333] hover:border-[#FF4444] transition-all">
                                <div className="text-[#FF4444] text-2xl mb-4">🔧</div>
                                <h3 className="text-xl font-bold text-white mb-2">Unlimited Commands</h3>
                                <p className="text-sm text-[#999999] mb-4">Create unlimited custom commands for your bot, compared to just 3 commands on the free plan.</p>
                                <div className="h-1 w-16 bg-gradient-to-r from-[#FF4444] to-[#FF6666]"></div>
                            </div>

                            {/* Unlimited Custom Events */}
                            <div className="bg-[#1A1A1A] p-6 rounded-lg border border-[#333333] hover:border-[#FF4444] transition-all">
                                <div className="text-[#FF4444] text-2xl mb-4">⚙️</div>
                                <h3 className="text-xl font-bold text-white mb-2">Unlimited Events</h3>
                                <p className="text-sm text-[#999999] mb-4">Create unlimited custom events for your bot, compared to just 3 events on the free plan.</p>
                                <div className="h-1 w-16 bg-gradient-to-r from-[#FF4444] to-[#FF6666]"></div>
                            </div>

                            {/* Premium Discord Role */}
                            <div className="bg-[#1A1A1A] p-6 rounded-lg border border-[#333333] hover:border-[#FF4444] transition-all">
                                <div className="text-[#FF4444] text-2xl mb-4">👑</div>
                                <h3 className="text-xl font-bold text-white mb-2">Premium Discord Role</h3>
                                <p className="text-sm text-[#999999] mb-4">Get an exclusive role in our Discord server with access to premium support channels and special perks.</p>
                                <div className="h-1 w-16 bg-gradient-to-r from-[#FF4444] to-[#FF6666]"></div>
                            </div>
                        </div>
                    </div>

                    {/* Premium Support Section */}
                    <PremiumSupport />

                    {/* Feature Comparison Table */}
                    <div className="mb-16">
                        <PricingTable />
                    </div>

                    {/* FAQ Section */}
                    <div className="mb-16">
                        <SectionHeader title="Frequently Asked Questions" pretitle="Premium" />
                        <div className="space-y-4 mt-8">
                            <Accordion
                                title="What do I get from BotGhost premium?"
                                description="BotGhost premium allows you access to all current and future modules, you may also set your own status message, removing the 'Made with botghost.com' message from your bot, premium bots also do not share hosting with bots that are on a free trial, meaning that they are allocated more resources and are generally faster than their non-premium counterparts."
                            />

                            <Accordion
                                title="Can I get a refund if I am not satisfied?"
                                description="Of course! If you are not satisfied with your premium subscription simply contact us within 7 days and we will give you a full refund, no questions asked."
                            />

                            <Accordion
                                title="How do I stop my subscription?"
                                description="Simply click the 'Manage Subscription' button above on this page. From there you can cancel your subscription or update your payment method. If you do decide to cancel your subscription, your bot will stay online for the entire period you paid for, this means you can buy a premium subscription and cancel it straight away if you only want one month of premium."
                            />

                            <Accordion
                                title="How many Discord Servers is my subscription valid for?"
                                description="Your premium subscription is valid for EVERY server that your bot is in. A Single plan subscription is valid for ONE bot only and an Unlimited subscription is valid for UNLIMITED bots."
                            />

                            <Accordion
                                title="What payment methods are supported?"
                                description="We support all major credit cards through Stripe, our secure payment processor. If your card is declined, please ensure your billing information is correct and that your card is enabled for online transactions."
                            />

                            <Accordion
                                title="Is my payment information secure?"
                                description="Absolutely. We use Stripe, a secure payment processor that handles billions of dollars in online transactions every year. We never store your credit card information on our servers."
                            />

                            <Accordion
                                title="How does my bot get upgraded after purchasing a subscription?"
                                description="Once you have purchased a subscription, your bot will be automatically upgraded to premium. For Single plan subscriptions, the bot you selected during checkout will be upgraded. For Unlimited plans, all of your bots will be upgraded automatically."
                            />

                            <Accordion
                                title="Can I pay in a different currency?"
                                description="All prices are displayed in USD. You can still pay in your own currency however, as Stripe will do the conversion for you."
                            />
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="bg-gradient-to-br from-[#1E1E1E] to-[#2A1A1A] rounded-xl p-8 text-center border border-[#FF4444] shadow-lg">
                        <h2 className="text-3xl font-bold text-white mb-4">Ready to Upgrade Your Bot?</h2>
                        <p className="text-[#999999] mb-8 max-w-2xl mx-auto">
                            Join thousands of premium users today and take your Discord bot to the next level with premium features, enhanced performance, and dedicated support.
                        </p>
                        {this.state.saleActive && (
                            <div className="bg-[#1A1A1A] p-3 rounded-md mb-6 max-w-2xl mx-auto border border-[#9d4edd]">
                                <p className="text-[#FFD700] font-bold">
                                    Lock in this discount FOREVER until you cancel!
                                </p>
                            </div>
                        )}
                        <button
                            onClick={() => this.handleSubscriptionAction(this.state.unlimitedBots ? 'unlimited' : 'single')}
                            disabled={this.state.processing || this.props.collab}
                            className="py-3 px-8 bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFD700] text-black font-bold rounded-md transition-all transform hover:-translate-y-1 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed">
                            {this.props.collab ? 'Not Available in Collab Mode' : this.state.processing ? 'Processing...' : 'Upgrade to Premium'}
                        </button>
                        <p className="text-xs text-[#999999] mt-4">
                            Includes 7-day money-back guarantee
                        </p>
                    </div>
                </div>
            </Dashboard>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.data.user,
    auth: state.auth,
    active_sale: state.data.active_sale,
    personal_sale: state.data.user?.personal_sale,
    bot: state.data.bot,
    collab: state.data.collab
});

const mapDispatchToProps = {
    setAuth
};

export default connect(mapStateToProps, mapDispatchToProps)(PremiumTesting); 